import { User } from './index';

/**
 * TypeScript Types for Maintenance Planning System
 * 
 * Comprehensive type definitions for maintenance schedules, work orders,
 * routes, and flexible task definitions matching the backend API.
 */

// Centralized StandardTaskAssignment interface to match API response
export interface StandardTaskAssignment {
  id: string;
  
  // Foreign key references
  standard_task: number;  // FK to StandardTask
  equipment: number;      // FK to Equipment
  
  // Flattened StandardTask information (from backend serializer)
  task_name: string;
  task_type: string;
  
  // Equipment information
  equipment_name: string;
  equipment_code: string;
  
  // Scheduling fields
  frequency_type: string;
  frequency_value: number;
  next_due_date: string;
  last_completed_date: string | null;
  is_active: boolean;
  
  // Planning parameters
  advance_notice_days: number;
  grace_period_days: number;
  
  // Effective values
  effective_description: string;
  effective_duration_hours: number;
  effective_crew_size: number;
  frequency_display: string;
  days_until_due: number;
  is_overdue: boolean;
  
  // Site information
  site_name: string;
}

// Re-export existing interfaces
export * from './index';

// Enums matching backend choices
export enum FrequencyType {
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
  SEMI_ANNUAL = 'semi_annual',
  ANNUAL = 'annual',
  HOURS_BASED = 'hours_based',
  CYCLES_BASED = 'cycles_based',
  CONDITION_BASED = 'condition_based'
}

export enum WorkOrderStatus {
  PLANNED = 'planned',
  SCHEDULED = 'scheduled',
  IN_PROGRESS = 'in_progress',
  ON_HOLD = 'on_hold',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled'
}

export enum Priority {
  LOW = 'low',
  NORMAL = 'normal',
  HIGH = 'high',
  URGENT = 'urgent',
  CRITICAL = 'critical'
}

export enum TaskInputType {
  CHECKBOX = 'checkbox',
  TEXT = 'text',
  NUMBER = 'number',
  MEASUREMENT = 'measurement',
  DROPDOWN = 'dropdown',
  MULTI_SELECT = 'multi_select',
  DATE = 'date',
  TIME = 'time',
  PHOTO = 'photo',
  SIGNATURE = 'signature'
}

export enum RouteType {
  VISUAL_INSPECTION = 'visual_inspection',
  LUBRICATION = 'lubrication',
  CALIBRATION = 'calibration',
  SAFETY_CHECK = 'safety_check',
  PERFORMANCE_TEST = 'performance_test',
  GENERAL_MAINTENANCE = 'general_maintenance'
}

export enum ServiceType {
  DAILY_CHECKS = 'daily_checks',
  WEEKLY_INSPECTION = 'weekly_inspection',
  MONTHLY_SERVICE = 'monthly_service',
  QUARTERLY_MAINTENANCE = 'quarterly_maintenance',
  ANNUAL_OVERHAUL = 'annual_overhaul',
  OUTAGE_MAINTENANCE = 'outage_maintenance'
}

export enum RouteStatus {
  PLANNED = 'planned',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  PARTIALLY_COMPLETED = 'partially_completed',
  CANCELLED = 'cancelled'
}

// Basic Equipment interface for maintenance context
export interface Equipment {
  id: number;
  name: string;
  kks_code?: string;
  equipment_id?: string;
  description?: string;
  manufacturer?: string;
  model?: string;
  serial_number?: string;
}

// Task Data Response interface for maintenance records
export interface TaskDataResponse {
  id: number;
  data_field_label: string;
  data_field_name: string;
  input_type: string;
  response_value: string;
  display_value?: string;
  numeric_value?: number;
  boolean_value?: boolean;
  date_value?: string;
  time_value?: string;
  unit_of_measure?: string;
  responded_at: string;
  responded_by: number;
}

// Standard Task interface
export interface StandardTask {
  id: number;
  task_name: string;
  task_code?: string;
  task_type: string;
  description: string;
  requires_isolation: boolean;
  requires_permit: boolean;
  estimated_duration_hours?: number;
}

// Core Maintenance Types
export interface MaintenanceSchedule {
  id: number;
  equipment: number;
  standard_task: number;
  frequency_type: FrequencyType;
  frequency_value: number;
  next_due_date: string;
  last_completed_date?: string;
  hours_based_trigger?: number;
  cycles_based_trigger?: number;
  advance_notice_days: number;
  grace_period_days: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  
  // Read-only fields from serializer
  name?: string;
  description?: string;
  equipment_name?: string;
  equipment_code?: string;
  task_name?: string;
  frequency_display?: string;
  site_name?: string;
}

export interface MaintenanceTaskDefinition {
  id: number;
  standard_task: number;
  field_name: string;
  field_label: string;
  input_type: TaskInputType;
  is_required: boolean;
  placeholder_text?: string;
  help_text?: string;
  options: string[];
  unit_of_measure?: string;
  min_value?: number;
  max_value?: number;
  display_order: number;
  section_name?: string;
  created_at: string;
  updated_at: string;
  
  // Read-only fields
  input_type_display?: string;
}

export interface MaintenanceTaskResponse {
  id: number;
  work_order?: number;
  route_work_order?: number;
  task_definition: number;
  equipment?: number;
  response_value?: string;
  numeric_value?: number;
  boolean_value?: boolean;
  date_value?: string;
  time_value?: string;
  file_path?: string;
  responded_by: number;
  responded_at: string;
  is_within_acceptable_range?: boolean;
  requires_follow_up: boolean;
  follow_up_notes?: string;
  
  // Read-only nested data
  task_definition_detail?: MaintenanceTaskDefinition;
  responded_by_detail?: User;
  equipment_name?: string;
}

export interface MaintenancePart {
  id: number;
  work_order: number;
  part_number: string;
  part_name: string;
  quantity_used: number;
  unit_of_measure: string;
  unit_cost?: number;
  total_cost?: number;
  supplier?: string;
  serial_number?: string;
  created_at: string;
}

export interface MaintenanceWorkOrder {
  id: number;
  work_order_number: string;
  equipment: number;
  maintenance_schedule: number;
  standard_task: number;
  planned_start_date: string;
  planned_end_date: string;
  scheduled_start_time?: string;
  scheduled_end_time?: string;
  actual_start_time?: string;
  actual_end_time?: string;
  status: WorkOrderStatus;
  priority: Priority;
  assigned_to?: number;
  supervisor?: number;
  title?: string;
  description?: string;
  work_description: string;
  special_instructions?: string;
  safety_notes?: string;
  estimated_hours?: number;
  required_crew_size: number;
  required_parts?: string;
  required_tools?: string;
  permit_required: boolean;
  related_permit?: number;
  completion_notes?: string;
  findings?: string;
  recommendations?: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  
  // Read-only nested data
  equipment_name?: string;
  equipment_code?: string;
  task_name?: string;
  assigned_to_detail?: User;
  supervisor_detail?: User;
  status_display?: string;
  priority_display?: string;
  task_responses?: MaintenanceTaskResponse[];
  parts_used?: MaintenancePart[];
  maintenance_record?: {
    id: number;
    work_performed: string;
    findings: string;
    work_quality_rating?: number;
    supervisor_approval: boolean;
  };
}

export interface MaintenanceRouteItem {
  id: number;
  route: number;
  equipment: number;
  standard_task: number;
  sequence_order: number;
  estimated_time_minutes: number;
  specific_instructions?: string;
  inspection_points: string[];
  critical_measurements: string[];
  acceptable_ranges: Record<string, any>;
  created_at: string;
  
  // Read-only fields
  equipment_name?: string;
  equipment_code?: string;
  task_name?: string;
}

export interface MaintenanceRoute {
  id: number;
  route_name: string;
  route_description: string;
  route_type: RouteType;
  frequency_type: FrequencyType;
  frequency_value: number;
  estimated_duration_hours: number;
  required_crew_size: number;
  required_tools?: string;
  safety_requirements?: string;
  optimal_start_location?: string;
  route_instructions?: string;
  site: number;
  is_active: boolean;
  created_by: number;
  created_at: string;
  updated_at: string;
  
  // Read-only nested data
  site_name?: string;
  site_code?: string;
  route_type_display?: string;
  frequency_display?: string;
  created_by_detail?: User;
  route_items?: MaintenanceRouteItem[];
}

export interface MaintenanceRouteWorkOrder {
  id: number;
  maintenance_route: number;
  route_work_order_number: string;
  planned_execution_date: string;
  scheduled_start_time?: string;
  scheduled_end_time?: string;
  actual_start_time?: string;
  actual_end_time?: string;
  assigned_crew: number[];
  route_leader?: number;
  status: RouteStatus;
  overall_completion_percentage: number;
  route_summary_notes?: string;
  issues_encountered?: string;
  equipment_bypassed: number[];
  permits_required: boolean;
  related_permits: number[];
  created_at: string;
  updated_at: string;
  completed_at?: string;
  
  // Read-only nested data
  maintenance_route_detail?: MaintenanceRoute;
  route_leader_detail?: User;
  assigned_crew_detail?: User[];
  status_display?: string;
  task_responses?: MaintenanceTaskResponse[];
}

export interface RoutineServiceWork {
  id: number;
  service_name: string;
  service_type: ServiceType;
  description: string;
  frequency_type: FrequencyType;
  frequency_value: number;
  maintenance_tasks: number[];
  estimated_total_hours: number;
  required_crew_size: number;
  required_specialist_skills?: string;
  site: number;
  is_active: boolean;
  created_by: number;
  created_at: string;
  updated_at: string;
  
  // Read-only fields
  site_name?: string;
  site_code?: string;
  service_type_display?: string;
  frequency_display?: string;
  created_by_detail?: User;
  tasks_count?: number;
}

export interface RoutineServiceWorkOrder {
  id: number;
  routine_service: number;
  service_work_order_number: string;
  planned_start_date: string;
  planned_end_date: string;
  scheduled_start_time?: string;
  scheduled_end_time?: string;
  actual_start_time?: string;
  actual_end_time?: string;
  assigned_crew: number[];
  service_leader?: number;
  status: WorkOrderStatus;
  overall_completion_percentage: number;
  summary_notes?: string;
  created_at: string;
  updated_at: string;
  
  // Read-only nested data
  routine_service_detail?: RoutineServiceWork;
  service_leader_detail?: User;
  assigned_crew_detail?: User[];
  status_display?: string;
}

export interface MaintenanceRecord {
  id: number;
  work_order: number | {
    id: number;
    work_order_number: string;
    status: string;
    priority: string;
  };
  equipment: number;
  performed_by: number;
  work_performed: string;
  findings?: string;
  issues_discovered?: string;
  parts_replaced?: string;
  measurements_taken: Record<string, any>;
  test_results: Record<string, any>;
  recommendations?: string;
  follow_up_required: boolean;
  follow_up_due_date?: string;
  follow_up_notes?: string;
  work_quality_rating?: number;
  supervisor_approval: boolean;
  approved_by?: number;
  approved_at?: string;
  photos: string[];
  documents: string[];
  created_at: string;
  updated_at: string;
  
  // Additional fields for records management
  maintenance_task?: {
    id: number;
    task_name: string;
    task_type: string;
    description: string;
  };
  completion_status: 'completed' | 'partial' | 'draft' | 'in_progress';
  completed_by: number;
  completed_by_detail?: User;
  completed_at: string;
  
  // Read-only nested data
  work_order_detail?: {
    work_order_number: string;
    status: string;
    priority: string;
  };
  equipment_name?: string;
  equipment_code?: string;
  performed_by_detail?: User;
  approved_by_detail?: User;
  
  // Enhanced serializer fields
  completion_status_display?: string;
  data_responses?: TaskDataResponse[];
}

// Summary types for dashboard and lists
export interface MaintenanceScheduleSummary {
  id: number;
  equipment_name: string;
  task_name: string;
  frequency_display: string;
  next_due_date: string;
  days_until_due: number;
  is_active: boolean;
}

export interface MaintenanceWorkOrderSummary {
  id: number;
  work_order_number: string;
  equipment_name: string;
  status: WorkOrderStatus;
  status_display: string;
  priority: Priority;
  priority_display: string;
  planned_start_date: string;
  assigned_to_name?: string;
}

export interface MaintenanceRouteSummary {
  id: number;
  route_name: string;
  route_type_display: string;
  site_name: string;
  estimated_duration_hours: number;
  items_count: number;
  is_active: boolean;
}

// Dashboard data interface
export interface MaintenanceDashboard {
  overdue_schedules: MaintenanceScheduleSummary[];
  upcoming_schedules: MaintenanceScheduleSummary[];
  active_work_orders: MaintenanceWorkOrderSummary[];
  recent_completions: MaintenanceWorkOrderSummary[];
  total_schedules: number;
  total_work_orders: number;
  overdue_count: number;
  due_this_week: number;
  active_work_orders_count: number;
  completed_today: number;
  completion_rate: number;
}

// Bulk operation types
export interface BulkWorkOrderUpdate {
  work_order_ids: number[];
  action: 'assign' | 'reschedule' | 'set_priority' | 'set_status';
  assigned_to?: number;
  supervisor?: number;
  new_date?: string;
  priority?: Priority;
  status?: WorkOrderStatus;
}

// API response types
export interface PaginatedResponse<T> {
  count: number;
  next?: string;
  previous?: string;
  results: T[];
}

// Filter types for API requests
export interface MaintenanceScheduleFilters {
  site?: string;
  equipment_type?: string;
  overdue?: boolean;
  due_within_days?: number;
  frequency_type?: FrequencyType;
  is_active?: boolean;
  search?: string;
  ordering?: string;
  page?: number;
  page_size?: number;
}

export interface MaintenanceWorkOrderFilters {
  site?: string;
  assigned_to?: number;
  supervisor?: number;
  status?: WorkOrderStatus[];
  priority?: Priority[];
  date_range_after?: string;
  date_range_before?: string;
  permit_required?: boolean;
  search?: string;
  ordering?: string;
  page?: number;
  page_size?: number;
}

export interface MaintenanceRouteFilters {
  site?: string;
  route_type?: RouteType[];
  frequency_type?: FrequencyType[];
  created_by?: number;
  is_active?: boolean;
  search?: string;
  ordering?: string;
  page?: number;
  page_size?: number;
}

// Form types for creating/updating
export interface CreateMaintenanceSchedule {
  equipment: number;
  standard_task: number;
  frequency_type: FrequencyType;
  frequency_value: number;
  next_due_date: string;
  hours_based_trigger?: number;
  cycles_based_trigger?: number;
  advance_notice_days?: number;
  grace_period_days?: number;
}

export interface CreateMaintenanceWorkOrder {
  equipment: number;
  maintenance_schedule?: number;
  standard_task: number;
  planned_start_date: string;
  planned_end_date: string;
  status?: WorkOrderStatus;
  priority?: Priority;
  assigned_to?: number;
  supervisor?: number;
  title?: string;
  description?: string;
  work_description: string;
  special_instructions?: string;
  safety_notes?: string;
  estimated_hours?: number;
  required_crew_size?: number;
  required_parts?: string;
  required_tools?: string;
  permit_required?: boolean;
  scheduled_start?: string;
}

export interface CreateMaintenanceRoute {
  route_name: string;
  route_description: string;
  route_type: RouteType;
  frequency_type: FrequencyType;
  frequency_value: number;
  estimated_duration_hours: number;
  required_crew_size?: number;
  required_tools?: string;
  safety_requirements?: string;
  optimal_start_location?: string;
  route_instructions?: string;
  site: number;
}

export interface CreateMaintenanceTaskDefinition {
  standard_task: number;
  field_name: string;
  field_label: string;
  input_type: TaskInputType;
  is_required?: boolean;
  placeholder_text?: string;
  help_text?: string;
  options?: string[];
  unit_of_measure?: string;
  min_value?: number;
  max_value?: number;
  display_order?: number;
  section_name?: string;
} 

// Enhanced maintenance task interfaces with overdue assignment tracking

export interface AssignmentCompletionStatus {
  total_assignments: number;
  completed_assignments: number;
  overdue_assignments: number;
  due_today_assignments: number;
  upcoming_assignments: number;
  completion_percentage: number;
}

export interface MaintenanceTask {
  id: string;
  task_name: string;
  task_description: string;
  site_name: string;
  site_code: string;
  next_due_date: string;
  days_until_due: number;
  is_overdue: boolean;
  frequency_display: string;
  priority: string;
  priority_display: string;
  status: string;
  status_display: string;
  
  // Assignment tracking
  total_assignments_count: number;
  due_assignments_count: number;
  has_overdue_assignments: boolean;
  overdue_assignments_count: number;
  is_partially_overdue: boolean;
  assignment_completion_status: AssignmentCompletionStatus;
  
  // Detailed assignment data
  task_assignments_detail: StandardTaskAssignment[];
  overdue_assignments_detail: StandardTaskAssignment[];
  
  // Resource information
  estimated_total_duration_hours: number;
  required_crew_size: number;
  
  // Metadata
  created_at: string;
  updated_at: string;
}

export interface MaintenanceTaskSummary {
  id: string;
  task_name: string;
  site_name: string;
  next_due_date: string;
  days_until_due: number;
  is_overdue: boolean;
  frequency_display: string;
  priority: string;
  priority_display: string;
  estimated_total_duration_hours: number;
  required_crew_size: number;
  
  // Assignment summary
  due_assignments_count: number;
  total_assignments_count: number;
  has_overdue_assignments: boolean;
  overdue_assignments_count: number;
  is_partially_overdue: boolean;
  assignment_completion_status: AssignmentCompletionStatus;
}

export interface CompletionTaskInfo {
  id: string;
  task_name: string;
  next_due_date: string;
  is_overdue: boolean;
  is_partially_overdue: boolean;
  has_overdue_assignments: boolean;
}

export interface CompletionStatusResponse {
  task_info: CompletionTaskInfo;
  completion_status: AssignmentCompletionStatus;
  due_assignments: StandardTaskAssignment[];
  overdue_assignments: StandardTaskAssignment[];
}

export interface AssignmentCompletionRequest {
  assignment_id: string;
  completion_date?: string;
}

export interface MultipleAssignmentCompletionRequest {
  assignment_ids: string[];
  completion_date?: string;
}

export interface AssignmentCompletionResponse {
  success: boolean;
  message?: string;
  error?: string;
  completion_status: AssignmentCompletionStatus;
}

export interface MultipleAssignmentCompletionResponse {
  success: boolean;
  completed_count: number;
  failed_count: number;
  completed_assignments: string[];
  failed_assignments: Array<[string, string]>;
  completion_status: AssignmentCompletionStatus;
}

// Calendar event interface with overdue assignment support
export interface MaintenanceCalendarEvent {
  id: string;
  title: string;
  start: string;
  allDay: boolean;
  backgroundColor: string;
  borderColor: string;
  textColor: string;
  extendedProps: {
    task: MaintenanceTaskSummary;
    hasOverdueAssignments: boolean;
    isPartiallyOverdue: boolean;
    overdueAssignmentsCount: number;
    completionPercentage: number;
  };
} 